import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Badge,
  Box,
  Button,
  Flex,
  SimpleGrid,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
} from "@chakra-ui/react";
import { brandPalette } from "../../../constants/brandPalette";
import CTable from "../../../components/Table/table";
import Layout from "../../../components/dashboard/Layout";
import LogoutMenu from "../../../components/dashboard/Layout/logout-menu";
import { adminSelectStyles } from "../../../components/Models/admin-modal-theme";
import { getStudentExamReportByChapterRequest } from "../../../modules/admin-modules/student-exams/Actions";
import { getAllChaptersRequest } from "../../../modules/admin-modules/chapters/Actions";
import { getAssistantsRequest } from "../../../modules/admin-modules/teachersAssistant/Actions";
import Select from "react-select";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { RiFileExcel2Line } from "react-icons/ri";

function sanitizeExcelSheetName(name) {
  let s = String(name);
  for (const c of ["\\", "/", "*", "?", ":", "[", "]"]) {
    s = s.split(c).join(" ");
  }
  s = s.trim().replace(/\s+/g, " ");
  return (s || "Sheet").slice(0, 31);
}

const selectBase = adminSelectStyles({ menuZIndex: 1600 });

const selectStyles = {
  ...selectBase,
  menuPortal: (base) => ({ ...base, zIndex: 1600 }),
  control: (styles, state) => ({
    ...selectBase.control(styles, state),
    borderRadius: "14px",
    minHeight: "44px",
  }),
};

const cardSx = {
  border: "1px solid",
  borderColor: "var(--border-subtle)",
  bg: "var(--surface-raised)",
  borderRadius: "18px",
  boxShadow: "0 18px 55px rgba(0,0,0,0.12)",
  color: "var(--text-primary)",
};

function StatusBadge({ isPositive, label }) {
  return (
    <Badge
      px={2}
      py={0.5}
      borderRadius="md"
      fontWeight="700"
      fontSize="xs"
      bg={isPositive ? "rgba(0, 170, 141, 0.12)" : "rgba(155, 44, 44, 0.12)"}
      color={isPositive ? "#00AA8D" : "#9B2C2C"}
    >
      {label}
    </Badge>
  );
}

function groupRecordsByExam(records) {
  if (!Array.isArray(records)) return {};

  return records.reduce((acc, record) => {
    const examName = record.exam?.name || `Exam #${record.exam_id ?? "Unknown"}`;
    if (!acc[examName]) acc[examName] = [];
    acc[examName].push(record);
    return acc;
  }, {});
}

const ExamReportByChapter = () => {
  const dispatch = useDispatch();
  const [selectedChapter, setSelectedChapter] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [pageNumber, setPageNumber] = useState(0);
  const setPage = () => {};

  const examReportData = useSelector((state) => state.adminStudentExams.examReportByChapter);
  const isLoading = useSelector((state) => state.adminStudentExams.isLoading);
  const chapters = useSelector((state) => state.chapters.chapters);
  const assistants = useSelector((state) => state.teacherassistants.assistants);

  useEffect(() => {
    dispatch(getAllChaptersRequest());
    dispatch(getAssistantsRequest());
  }, [dispatch]);

  useEffect(() => {
    if (selectedChapter) {
      dispatch(getStudentExamReportByChapterRequest(selectedChapter.value));
    }
  }, [selectedChapter, dispatch]);

  const chapterOptions = useMemo(
    () =>
      chapters?.map((chapter) => ({
        value: chapter.id,
        label: chapter.name,
      })) ?? [],
    [chapters]
  );

  const reportRecords = useMemo(() => {
    if (Array.isArray(examReportData?.data)) return examReportData.data;
    if (Array.isArray(examReportData)) return examReportData;
    return [];
  }, [examReportData]);

  const groupedByExam = useMemo(() => groupRecordsByExam(reportRecords), [reportRecords]);

  const examNames = useMemo(() => Object.keys(groupedByExam), [groupedByExam]);

  const assistantStats = useMemo(() => {
    const stats = {};
    let totalChecked = 0;

    reportRecords.forEach((record) => {
      if (record.assistant) {
        const assistantName = record.assistant.name || "Unknown";
        stats[assistantName] = (stats[assistantName] || 0) + 1;
        totalChecked++;
      }
    });

    return { stats, totalChecked };
  }, [reportRecords]);

  const getCurrentTabData = () => {
    if (examNames.length === 0) return [];

    const currentExamName = examNames[activeTab];
    const examRecords = groupedByExam[currentExamName] || [];

    return examRecords.map((record, index) => ({
      ...record,
      index: `#${index + 1}`,
      student_name: record.student?.name || "N/A",
      student_username: record.student?.user_name || "N/A",
      right_answers: record.right_answers ?? "—",
      wrong_answers: record.wrong_answers ?? "—",
      assistant_name: record.assistant?.name || "No assistant",
      correction_badge: (
        <StatusBadge
          isPositive={record.correction === 1}
          label={record.correction === 1 ? "Corrected" : "Pending"}
        />
      ),
      pdf_badge: (
        <StatusBadge
          isPositive={!!record.student_pdf}
          label={record.student_pdf ? "Submitted" : "No PDF"}
        />
      ),
    }));
  };

  const currentTabData = getCurrentTabData();

  useEffect(() => {
    if (examNames.length > 0 && activeTab >= examNames.length) {
      setActiveTab(0);
    }
  }, [examNames.length, activeTab]);

  const columns = [
    { Header: "ID", accessor: "index" },
    { Header: "Student", accessor: "student_name" },
    { Header: "Username", accessor: "student_username" },
    { Header: "Right", accessor: "right_answers" },
    { Header: "Wrong", accessor: "wrong_answers" },
    { Header: "Assistant", accessor: "assistant_name" },
    { Header: "Correction", accessor: "correction_badge" },
    { Header: "PDF", accessor: "pdf_badge" },
  ];

  const exportToExcel = async () => {
    if (examNames.length === 0) return;

    const workbook = new ExcelJS.Workbook();

    Object.entries(groupedByExam).forEach(([examName, records]) => {
      if (!Array.isArray(records) || records.length === 0) return;

      const sheet = workbook.addWorksheet(sanitizeExcelSheetName(examName));

      sheet.columns = [
        { header: "Student Name", key: "studentName", width: 25 },
        { header: "Student Username", key: "studentUsername", width: 15 },
        { header: "Right Answers", key: "rightAnswers", width: 15 },
        { header: "Wrong Answers", key: "wrongAnswers", width: 15 },
        { header: "Correction", key: "correction", width: 15 },
        { header: "Assistant Name", key: "assistantName", width: 25 },
        { header: "PDF", key: "pdf", width: 15 },
      ];

      records.forEach((record) => {
        sheet.addRow({
          studentName: record.student?.name || "N/A",
          studentUsername: record.student?.user_name || "N/A",
          rightAnswers: record.right_answers ?? "—",
          wrongAnswers: record.wrong_answers ?? "—",
          correction: record.correction === 1 ? "Corrected" : "Pending",
          assistantName: record.assistant?.name || "No assistant",
          pdf: record.student_pdf ? "Submitted" : "No PDF",
        });
      });

      const sheetAssistantStats = {};
      records.forEach((record) => {
        if (record.assistant?.name) {
          const name = record.assistant.name;
          sheetAssistantStats[name] = (sheetAssistantStats[name] || 0) + 1;
        }
      });

      if (Object.keys(sheetAssistantStats).length > 0) {
        sheet.addRow({});
        sheet.addRow({ studentName: "Assistant statistics", studentUsername: "" });
        Object.entries(sheetAssistantStats).forEach(([name, count]) => {
          sheet.addRow({ studentName: name, studentUsername: count });
        });
      }
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const fileName = `exam-report-${
      selectedChapter?.label || "chapter"
    }-${new Date().toISOString().split("T")[0]}.xlsx`;
    saveAs(blob, fileName);
  };

  const assistantOverview = assistants?.data ?? [];

  return (
    <Layout>
      <Box
        minH="400px"
        maxW="1440px"
        mx="auto"
        w="100%"
        px={{ base: 4, sm: 5, md: 8, lg: 10 }}
        pt={{ base: 6, md: 8 }}
        pb={{ base: 10, md: 12 }}
        dir="ltr"
      >
        <Flex
          w="100%"
          mb={8}
          justifyContent="space-between"
          alignItems={{ base: "stretch", md: "flex-start" }}
          gap={4}
          flexWrap="wrap"
        >
          <Box flex="1" minW="0">
            <Text
              fontSize={{ base: "28px", md: "40px" }}
              fontWeight="800"
              color={brandPalette.primary}
              fontFamily="'Amiri', 'Tajawal', sans-serif"
              lineHeight="1.1"
              sx={{ "html[data-theme='dark'] &": { color: "var(--text-primary)" } }}
            >
              Exams Comprehensive Report
            </Text>
            <Text mt={2} fontSize="sm" fontWeight="600" color="var(--text-muted)" maxW="2xl">
              Review exam submissions by chapter, correction status, and assistant activity across
              all exams in the selected lesson.
            </Text>
          </Box>
          <Flex
            align="flex-start"
            gap={3}
            flexWrap="wrap"
            justifyContent={{ base: "flex-start", md: "flex-end" }}
          >
            <Box minW={{ base: "100%", md: "280px" }} maxW={{ base: "100%", md: "360px" }}>
              <Select
                options={chapterOptions}
                isMulti={false}
                onChange={setSelectedChapter}
                styles={selectStyles}
                placeholder="Select chapter"
                value={selectedChapter}
                menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                menuPosition="fixed"
                isClearable
              />
            </Box>
            {examNames.length > 0 ? (
              <Button
                onClick={exportToExcel}
                leftIcon={<RiFileExcel2Line />}
                bg={brandPalette.primary}
                color={brandPalette.cream}
                fontWeight="800"
                borderRadius="full"
                px={6}
                _hover={{ bg: brandPalette.accentDark }}
              >
                Export to Excel
              </Button>
            ) : null}
            <LogoutMenu />
          </Flex>
        </Flex>

        {assistantOverview.length > 0 ? (
          <Box {...cardSx} p={{ base: 4, md: 6 }} mb={6}>
            <Text fontSize="md" fontWeight="800" color="var(--text-primary)" mb={4}>
              Assistant overview
            </Text>
            <SimpleGrid columns={{ base: 1, sm: 2, md: 3, lg: 4 }} spacing={4}>
              {assistantOverview.map((assistant) => (
                <Box
                  key={assistant?.id}
                  p={4}
                  bg="var(--surface-muted)"
                  borderWidth="1px"
                  borderColor="var(--border-subtle)"
                  borderRadius="lg"
                >
                  <Text fontSize="xs" fontWeight="800" color="var(--text-muted)" textTransform="uppercase" mb={1}>
                    Assistant
                  </Text>
                  <Text fontSize="sm" fontWeight="700" color="var(--text-primary)" noOfLines={2} mb={2}>
                    {assistant?.name}
                  </Text>
                  <Text fontSize="2xl" fontWeight="800" color={brandPalette.primary} lineHeight="1">
                    {assistant?.assistant_points || 0}
                  </Text>
                  <Text fontSize="xs" color="var(--text-muted)" mt={1}>
                    Points
                  </Text>
                  <Flex mt={3} gap={2} flexWrap="wrap">
                    <Badge
                      px={2}
                      py={0.5}
                      borderRadius="md"
                      fontWeight="700"
                      fontSize="xs"
                      bg="rgba(91, 13, 22, 0.1)"
                      color={brandPalette.primary}
                    >
                      Exam {assistant?.exam_count || 0}
                    </Badge>
                    <Badge
                      px={2}
                      py={0.5}
                      borderRadius="md"
                      fontWeight="700"
                      fontSize="xs"
                      bg="rgba(91, 13, 22, 0.1)"
                      color={brandPalette.primary}
                    >
                      Quiz {assistant?.quiz_count || 0}
                    </Badge>
                    <Badge
                      px={2}
                      py={0.5}
                      borderRadius="md"
                      fontWeight="700"
                      fontSize="xs"
                      bg="rgba(91, 13, 22, 0.1)"
                      color={brandPalette.primary}
                    >
                      HW {assistant?.homework_count || 0}
                    </Badge>
                  </Flex>
                </Box>
              ))}
            </SimpleGrid>
          </Box>
        ) : null}

        {Object.keys(assistantStats.stats).length > 0 ? (
          <Box {...cardSx} p={{ base: 4, md: 6 }} mb={6}>
            <Flex align="baseline" justify="space-between" gap={3} mb={4} flexWrap="wrap">
              <Text fontSize="md" fontWeight="800" color="var(--text-primary)">
                Chapter assistant activity
              </Text>
              <Badge
                bg="rgba(91, 13, 22, 0.1)"
                color={brandPalette.primary}
                px={3}
                py={1}
                borderRadius="full"
                fontWeight="800"
              >
                {assistantStats.totalChecked} reviewed
              </Badge>
            </Flex>
            <SimpleGrid columns={{ base: 1, sm: 2, md: 3, lg: 4 }} spacing={4}>
              {Object.entries(assistantStats.stats).map(([assistantName, count]) => (
                <Box
                  key={assistantName}
                  p={4}
                  bg="var(--surface-muted)"
                  borderWidth="1px"
                  borderColor="var(--border-subtle)"
                  borderRadius="lg"
                >
                  <Text fontSize="xs" fontWeight="800" color="var(--text-muted)" textTransform="uppercase" mb={1}>
                    Assistant
                  </Text>
                  <Text fontSize="sm" fontWeight="700" color="var(--text-primary)" noOfLines={2} mb={2}>
                    {assistantName}
                  </Text>
                  <Text fontSize="2xl" fontWeight="800" color={brandPalette.primary} lineHeight="1">
                    {count}
                  </Text>
                  <Text fontSize="xs" color="var(--text-muted)" mt={1}>
                    In this chapter
                  </Text>
                </Box>
              ))}
            </SimpleGrid>
          </Box>
        ) : null}

        {examNames.length > 0 ? (
          <Box {...cardSx} p={{ base: 4, md: 6 }}>
            <Tabs index={activeTab} onChange={setActiveTab} variant="unstyled" isLazy>
              <TabList flexWrap="wrap" gap={2} mb={6}>
                {examNames.map((examName) => (
                  <Tab
                    key={examName}
                    px={4}
                    py={2}
                    borderRadius="full"
                    fontWeight="700"
                    fontSize="sm"
                    borderWidth="1px"
                    borderColor="var(--border-subtle)"
                    bg="var(--surface-muted)"
                    color="var(--text-muted)"
                    _selected={{
                      bg: brandPalette.primary,
                      color: brandPalette.cream,
                      borderColor: brandPalette.primary,
                    }}
                    _hover={{
                      borderColor: brandPalette.primary,
                      color: "var(--text-primary)",
                    }}
                  >
                    {examName}
                  </Tab>
                ))}
              </TabList>
              <TabPanels>
                {examNames.map((examName) => (
                  <TabPanel key={examName} px={0}>
                    <CTable
                      selectedData={() => {}}
                      Columns={columns}
                      Data={getCurrentTabData()}
                      Actions
                      currentpage={pageNumber}
                      setPage={setPage}
                      setPageNumber={setPageNumber}
                      totalPage={Math.ceil(currentTabData.length / 10) || 1}
                      isLoading={isLoading}
                      wrapperProps={{
                        borderWidth: "1px",
                        borderColor: "var(--border-subtle)",
                        borderRadius: "12px",
                        overflow: "hidden",
                      }}
                    />
                  </TabPanel>
                ))}
              </TabPanels>
            </Tabs>
          </Box>
        ) : null}

        {reportRecords.length === 0 && !isLoading ? (
          <Box {...cardSx} p={10} textAlign="center">
            <Text fontSize="md" fontWeight="700" color="var(--text-muted)">
              {selectedChapter
                ? "No exam data found for the selected chapter."
                : "Select a chapter to load the exam report."}
            </Text>
          </Box>
        ) : null}

        {isLoading ? (
          <Box {...cardSx} p={10} textAlign="center">
            <Text fontSize="md" fontWeight="700" color="var(--text-primary)">
              Loading report…
            </Text>
          </Box>
        ) : null}
      </Box>
    </Layout>
  );
};

export default ExamReportByChapter;
